/**
 * Account Manager Command Center (Task 206).
 *
 * The assigned-client follow-up & delivery surface for an agency
 * `account_manager` with MULTIPLE assigned clients. Rendered by `AppLanding`
 * ONLY when `isAccountManager(me)` is true, `me.client_assignments.length > 1`,
 * and the owner / marketing_manager gates did not fire first. A single-client
 * account_manager is bounced to that client (unchanged); a zero-client one
 * keeps the Task 200 scoped empty state. Display-only and gated upstream.
 *
 * SCOPE: this surface shows ASSIGNED clients ONLY — it reads
 * `me.client_assignments` and deliberately does NOT use `useClientScope`
 * (which, for leadership, would expose the whole org). No agency-wide
 * revenue/profit, no owner/marketing-manager content, no owner-management
 * controls, and assigned-client scoping is not weakened.
 *
 * Visual language: the shared `.occ-*` command-center styles (RTL,
 * cream/ink/tangerine), via an `occ amcc` root — content is account-manager
 * specific.
 *
 * Data posture (same honesty rule as the other command centers): real data is
 * used where `me.client_assignments` already provides it (active assigned-
 * client count, clients-needing-follow-up count, the assigned-client health
 * list, status-derived follow-ups). Delivery metrics with no live endpoint yet
 * (approvals, late tasks, content/reports this week, timeline, touchpoints,
 * escalations) render honest "—" placeholders / empty states labelled
 * «قيد الربط». No fabricated figures, no faked actions.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MeContext } from '../api/types';
import {
  SOON,
  avatarColor,
  buildFollowUps,
  clientStatusLabelAr,
  initial,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  toArabicDigits,
} from './accountManagerCommandCenter.logic';

// ── Inline icon set (24×24, currentColor stroke) ─────────────────────
type IconName =
  | 'search'
  | 'plus'
  | 'bell'
  | 'eye'
  | 'check'
  | 'clock'
  | 'calendar'
  | 'chart'
  | 'users'
  | 'bolt'
  | 'chev'
  | 'phone'
  | 'user-plus'
  | 'arrow-up'
  | 'message';

function Icon({ name, className }: { readonly name: IconName; readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      {iconBody(name)}
    </svg>
  );
}

function iconBody(name: IconName): ReactNode {
  switch (name) {
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </>
      );
    case 'plus':
      return <path d="M12 5v14M5 12h14" strokeLinecap="round" />;
    case 'bell':
      return (
        <path
          d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M13.7 21a2 2 0 0 1-3.4 0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'eye':
      return (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case 'check':
      return (
        <path
          d="M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" />
        </>
      );
    case 'calendar':
      return (
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
        </>
      );
    case 'chart':
      return <path d="M3 21V5M3 21h18M7 16v-6M12 16v-9M17 16v-3" strokeLinecap="round" />;
    case 'users':
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path
            d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M17 11a4 4 0 0 0 0-8M22 21c0-2.8-1.9-5.1-4.5-5.8"
            strokeLinecap="round"
          />
        </>
      );
    case 'bolt':
      return <path d="M13 2L3 14h8l-1 8 10-12h-8z" strokeLinejoin="round" />;
    case 'chev':
      return <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />;
    case 'phone':
      return (
        <path
          d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L16 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'user-plus':
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M19 8v6M22 11h-6" strokeLinecap="round" />
        </>
      );
    case 'arrow-up':
      return <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />;
    case 'message':
      return (
        <path
          d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    default:
      return null;
  }
}

interface AccountManagerCommandCenterProps {
  readonly me: MeContext;
}

export function AccountManagerCommandCenter({ me }: AccountManagerCommandCenterProps) {
  // ASSIGNED clients only — never the org-wide list.
  const assignments = me.client_assignments;
  const amName = me.profile?.full_name ?? me.profile?.email ?? 'مدير الحسابات';

  const activeCount = assignments.filter((a) => a.client_status === 'active').length;
  const needsFollowUp = assignments.filter(
    (a) => a.client_status === 'paused' || a.client_status === 'onboarding',
  ).length;

  const followUps = buildFollowUps(assignments);

  // Assigned-client health rows — needs-attention first. Cap at 8.
  const tableRows = [...assignments]
    .sort((a, b) => statusWeight(b.client_status) - statusWeight(a.client_status))
    .slice(0, 8);

  return (
    <div className="occ amcc" dir="rtl">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="occ-top">
        <div className="occ-search">
          <input type="search" placeholder="ابحث في عملائك المعيّنين…" aria-label="بحث في عملائك" />
          <Icon name="search" />
        </div>
        <div className="occ-top-actions">
          <div className="occ-range" role="group" aria-label="النطاق الزمني">
            <button type="button" className="active" aria-pressed="true">
              اليوم
            </button>
            <button type="button">هذا الأسبوع</button>
            <button type="button">الشهر</button>
          </div>
          <Link to="/app/clients" className="occ-create">
            <Icon name="plus" />
            إجراء سريع
          </Link>
          <Link
            to="/app/notifications"
            className="occ-iconbtn"
            title="الإشعارات"
            aria-label="الإشعارات"
          >
            <Icon name="bell" />
            <span className="occ-dot" />
          </Link>
        </div>
      </div>

      {/* ── Page header ─────────────────────────────────────────── */}
      <header className="occ-head">
        <h1 className="h-page">
          مركز متابعة{' '}
          <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
            العملاء
          </em>
        </h1>
        <p className="occ-sub">
          أهلاً {amName} — لديك {toArabicDigits(assignments.length)} عميل معيّن.{' '}
          {followUps.length > 0 ? (
            <>
              <b>{followUps.length} متابعة</b> بحاجة إلى إجراءك اليوم.
            </>
          ) : (
            'لا توجد متابعات عاجلة الآن.'
          )}{' '}
          <span className="occ-note-inline">بعض مؤشّرات التسليم قيد الربط بالبيانات المباشرة.</span>
        </p>
      </header>

      {/* ── Assigned-client KPI strip (6) ───────────────────────── */}
      <div className="occ-pulse">
        <span className="occ-blob" />
        <Kpi
          icon="eye"
          cap="عملائي النشطون"
          value={toArabicDigits(activeCount)}
          note={`من ${toArabicDigits(assignments.length)} معيّن`}
        />
        <Kpi icon="check" cap="موافقات بانتظار العميل" value={SOON} note="قيد الربط" />
        <Kpi icon="clock" cap="مهام متأخرة" value={SOON} note="قيد الربط" risk />
        <Kpi icon="calendar" cap="محتوى هذا الأسبوع" value={SOON} note="قيد الربط" />
        <Kpi icon="chart" cap="تقارير مستحقة" value={SOON} note="قيد الربط" />
        <Kpi
          icon="users"
          cap="عملاء يحتاجون متابعة"
          value={toArabicDigits(needsFollowUp)}
          note="متوقّف/قيد التأهيل"
          risk
        />
      </div>

      {/* ── Follow-ups centerpiece ──────────────────────────────── */}
      <section className="occ-decisions">
        <div className="occ-dec-head">
          <span className="occ-dh-ic">
            <Icon name="bolt" />
          </span>
          <span className="occ-dh-t">
            متابعات تحتاج <em>إجراءك</em> اليوم
          </span>
          <span className="occ-dh-c">
            {followUps.length > 0 ? `${toArabicDigits(followUps.length)} بنود` : 'لا بنود'}
          </span>
        </div>
        {followUps.length > 0 ? (
          followUps.map((d) => (
            <div key={d.key} className={`occ-dec ${d.tone}`}>
              <span className={`occ-sev ${d.sev}`}>
                <span className="occ-sd" />
                {d.sevLabel}
              </span>
              <span className="occ-d-ic">
                <Icon name={d.icon} />
              </span>
              <div className="occ-d-body">
                <div className="occ-d-t">{d.title}</div>
                <div className="occ-d-why">{d.why}</div>
              </div>
              <Link to={d.to} className="occ-d-act">
                {d.action}
                <Icon name="chev" />
              </Link>
            </div>
          ))
        ) : (
          <div className="occ-empty">
            ستظهر هنا متابعاتك مرتّبة حسب الأولوية — الموافقات المعلّقة لدى العملاء، المحتوى بانتظار
            الرد، التقارير المستحقة، والمهام المتأخّرة — فور ربط بيانات التسليم. لا يوجد ما يستدعي
            إجراءً عاجلاً الآن.
          </div>
        )}
      </section>

      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="occ-grid">
        <div>
          {/* Assigned client health */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                صحة <em>عملائك</em> المعيّنين
              </div>
              <span className="occ-card-link" aria-hidden="true">
                {toArabicDigits(assignments.length)} عميل
              </span>
            </div>
            {tableRows.length > 0 ? (
              <table className="occ-ctable">
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th className="c">العلاقة</th>
                    <th className="c">المحتوى</th>
                    <th className="c">الموافقات</th>
                    <th className="c">الخطر</th>
                    <th>الإجراء التالي</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((a) => {
                    const risk = statusRisk(a.client_status);
                    return (
                      <tr key={a.client_id}>
                        <td>
                          <div className="occ-cl-cell">
                            <span
                              className="occ-cl-av"
                              style={{ background: avatarColor(a.client_id) }}
                            >
                              {initial(a.client_name)}
                            </span>
                            <span>
                              <span className="occ-cl-nm">{a.client_name}</span>
                              <span className="occ-cl-sub">
                                <span className={`occ-badge ${statusBadgeClass(a.client_status)}`}>
                                  {clientStatusLabelAr(a.client_status)}
                                </span>
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c">
                          <span className={`occ-risk ${risk.cls}`}>
                            <span className="rd" />
                            {risk.label}
                          </span>
                        </td>
                        <td>
                          <Link to={`/app/clients/${a.client_id}/overview`} className="occ-next">
                            فتح المساحة <Icon name="chev" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="occ-empty">لم تُسنَد إليك أي عملاء حتى الآن.</div>
            )}
          </section>

          {/* Approval queue */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                طابور <em>الموافقات</em>
              </div>
              <Link to="/app/clients" className="occ-card-link">
                العملاء <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              ستظهر هنا العناصر المنتظرة لموافقة العميل أو الموافقة الداخلية — مع نوعها، العميل،
              ومدّة الانتظار، وإجراء تذكير/فتح/متابعة — فور ربط بيانات الموافقات.
            </div>
          </section>

          {/* This week timeline */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                جدول <em>هذا الأسبوع</em>
              </div>
              <Link to="/app/clients" className="occ-card-link">
                العملاء <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              سيظهر هنا المنشورات المجدولة، محطّات الحملات، مواعيد الإنتاج، التقارير المستحقة،
              الاجتماعات، وتذكيرات التجديد لعملائك المعيّنين — فور ربط بيانات الجدول.
            </div>
          </section>
        </div>

        <div>
          {/* Touchpoints */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                نقاط <em>التواصل</em>
              </div>
            </div>
            <div className="occ-empty sm">
              سيظهر هنا العملاء الذين لم تتواصلي معهم مؤخراً، الاجتماعات القادمة، آخر تفاعل، الردود
              المعلّقة، والإجراء المقترح التالي — فور ربط بيانات التواصل.
            </div>
          </section>

          {/* Escalations */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                تصعيد <em>المشكلات</em>
              </div>
            </div>
            <div className="occ-empty sm">
              ستظهر هنا البنود التي تتجاوز نطاقك — مخاطر التسرّب، تكرار تأخّر الموافقات، اختناق
              الإنتاج، ضعف أداء الحملات، توسّع النطاق، وشكاوى العملاء — للتصعيد إلى مدير التسويق
              أولاً (والمالك لمخاطر الحساب الجسيمة فقط). لا تتضمّن هذه المساحة أي إدارة لحساب
              المالك.
            </div>
          </section>

          {/* Quick actions */}
          <div className="occ-qa-card">
            <div className="occ-qa-label">إجراءات سريعة</div>
            <div className="occ-qa-row">
              <Link to="/app/clients" className="occ-qa">
                <Icon name="check" /> فتح الموافقات
              </Link>
              <Link to="/app/clients" className="occ-qa">
                <Icon name="calendar" /> تقويم المحتوى
              </Link>
              <Link to="/app/reports" className="occ-qa">
                <Icon name="chart" /> طلب تقرير
              </Link>
              <Link to="/app/paid-media" className="occ-qa">
                <Icon name="eye" /> الإعلانات المدفوعة
              </Link>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="message" /> إرسال تذكير
              </button>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="calendar" /> جدولة اجتماع
              </button>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="arrow-up" /> تصعيد مشكلة
              </button>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="phone" /> إضافة ملاحظة
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  icon,
  cap,
  value,
  note,
  risk,
}: {
  readonly icon: IconName;
  readonly cap: string;
  readonly value: string;
  readonly note: string;
  readonly risk?: boolean;
}) {
  return (
    <div className="occ-pl">
      <div className="occ-cap">
        <Icon name={icon} />
        {cap}
      </div>
      <div className={`occ-big${risk ? ' risk' : ''}`}>{value}</div>
      <div className="occ-delta">{note}</div>
    </div>
  );
}
